import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Nav } from "@/components/hirekit/Nav";
import { requestPasswordReset } from "@/lib/candidate-api";

export const Route = createFileRoute("/forgot-password")({
  head: () => ({
    meta: [
        { name: "robots", content: "noindex,nofollow" },
      { title: "Reset your password | CLOSDD" },
      { name: "description", content: "Enter your email and we'll send you a link to reset your CLOSDD password." },
      { property: "og:title", content: "Reset your password | CLOSDD" },
      { property: "og:description", content: "Enter your email and we'll send you a link to reset your CLOSDD password." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: ForgotPassword,
});

const inputClass =
  "w-full rounded-full border border-ink/15 bg-white px-5 py-3.5 text-[16px] text-ink outline-none focus:border-purple-strong";

function ForgotPassword() {
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  return (
    <div className="cl min-h-screen bg-cream">
      <Nav />
      <main className="mx-auto w-full max-w-[520px] px-5 pb-20 pt-12">
        <Link to="/find-jobs" className="text-[12px] font-bold uppercase tracking-[0.24em] text-purple-strong">
          ← Back to jobs
        </Link>

        {sentTo ? (
          <>
            <h1 className="mt-5 font-serif text-[25px] leading-[1.05] tracking-[-0.02em] text-ink sm:text-[32px]">
              Check your email
            </h1>
            <p className="mt-3 text-[17px] leading-[1.6] text-ink/65">
              We've sent a password reset link to <span className="font-medium text-ink">{sentTo}</span>. Open it and
              choose a new password.
            </p>
            <div className="mt-8 rounded-[20px] border border-purple-strong/15 bg-purple-soft/50 p-6">
              <p className="text-[12px] font-bold uppercase tracking-[0.2em] text-purple-strong">Didn't get it?</p>
              <p className="mt-2 text-[15px] leading-[1.6] text-ink/65">
                Check your spam folder, then try again with the same email.
              </p>
              <button
                type="button"
                onClick={() => setSentTo(null)}
                className="mt-4 rounded-full border border-purple-strong/30 px-6 py-3 text-[12px] font-bold uppercase tracking-[0.16em] text-purple-strong transition hover:bg-white"
              >
                Use a different email
              </button>
            </div>
          </>
        ) : (
          <>
            <h1 className="mt-5 font-serif text-[25px] leading-[1.05] tracking-[-0.02em] text-ink sm:text-[32px]">
              Reset your password
            </h1>
            <p className="mt-3 text-[17px] leading-[1.6] text-ink/65">
              Enter your email and we'll send you a link to choose a new password.
            </p>

            <form
              className="mt-8 grid gap-5"
              onSubmit={(e) => {
                e.preventDefault();
                const email = String(new FormData(e.currentTarget).get("email") ?? "").trim();
                if (!email || sending) return;
                setError("");
                setSending(true);
                requestPasswordReset(email)
                  .then(() => setSentTo(email))
                  .catch((err) => setError(err instanceof Error ? err.message : "Something went wrong. Try again."))
                  .finally(() => setSending(false));
              }}
            >
              <label className="grid gap-2">
                <span className="text-[12px] font-bold uppercase tracking-[0.18em] text-ink/50">Email</span>
                <input required type="email" name="email" placeholder="you@email.com" className={inputClass} />
              </label>
              {error ? <p className="text-[14px] font-semibold text-ink/70">{error}</p> : null}
              <button
                type="submit"
                disabled={sending}
                className="justify-self-start rounded-full bg-purple-strong px-8 py-4 text-[13px] font-bold uppercase tracking-[0.16em] text-white transition hover:-translate-y-0.5 disabled:opacity-60"
              >
                {sending ? "Sending…" : "Send reset link"}
              </button>
            </form>
          </>
        )}

        <p className="mt-6 text-[14px] text-ink/50">
          Remember your password?{" "}
          <Link to="/find-jobs" className="font-medium text-purple-strong underline underline-offset-4 hover:text-ink">
            Back to jobs
          </Link>
        </p>
      </main>
    </div>
  );
}
